import store from '@state/store'
import { cloneDeep } from 'lodash'

// auth related routes
const authRoutes = [
  {
    path: '/login',
    name: 'login',
    component: () => lazyLoadView(import('@views/pages/account/login')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          const redirectFrom = routeTo.query.redirectFrom
          if (redirectFrom === 'support_ticket') {
            const data = JSON.parse(localStorage.getItem('auth.currentUser'))
            const fullUrl = `https://support.plasgate.com/auth?token=${encodeURIComponent(
              data.refreshToken
            )}`

            window.location.href = fullUrl
          } else {
            next({ name: 'Dashboard' })
          }
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/register',
    name: 'register',
    component: () => lazyLoadView(import('@views/pages/account/register')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/confirm-account',
    name: 'confirm-account',
    component: () => lazyLoadView(import('@views/pages/account/confirm')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/new-password',
    name: 'new-password',
    component: () => lazyLoadView(import('@views/pages/account/newPassword')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/recovery/:token',
    name: 'recovery',
    component: () => lazyLoadView(import('@views/pages/account/recovery')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/forget-password',
    name: 'forget-password',
    component: () =>
      lazyLoadView(import('@views/pages/account/forgetPassword')),
    meta: {
      beforeResolve(routeTo, routeFrom, next) {
        // If the user is already logged in
        if (store.getters['auth/loggedIn']) {
          // Redirect to the home page instead
          next({ name: 'Dashboard' })
        } else {
          // Continue to the login page
          next()
        }
      },
    },
  },
  {
    path: '/logout',
    name: 'logout',
    meta: {
      authRequired: true,
      beforeResolve(routeTo, routeFrom, next) {
        store.dispatch('auth/logOut')
        const authRequiredOnPreviousRoute = routeFrom.matched.some(
          (route) => route.meta.authRequired
        )
        // Navigate back to previous page, or home as a fallback
        next(
          authRequiredOnPreviousRoute ? { name: 'Dashboard' } : { ...routeFrom }
        )
      },
    },
  },
]

// error pages
const errorPagesRoutes = [
  {
    path: '/403',
    name: '403',
    component: require('@views/pages/secondary/error-403').default,
    props: true,
  },
  {
    path: '/404',
    name: '404',
    component: require('@views/pages/secondary/error-404').default,
    // Allows props to be passed to the 404 page through route
    // params, such as `resource` to define what wasn't found.
    props: true,
  },
  {
    path: '/500',
    name: '500',
    component: require('@views/pages/secondary/error-500').default,
    props: true,
  },
  // Redirect any unmatched routes to the 404 page. This may
  // require some server configuration to work in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  {
    path: '*',
    redirect: '404',
  },
]

// dashboard
const dashboardRoutes = [
  {
    path: '/',
    name: 'Dashboard',
    icon: 'uil-home',
    header: 'General',
    component: () => lazyLoadView(import('@views/pages/dashboard/dashboard')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]
const activityRoutes = [
  {
    name: 'Activity',
    path: '/activity',
    icon: 'uil-file-upload-alt',
    component: () => lazyLoadView(import('@views/pages/log/activity_log')),
    meta: { authRequired: true },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

// SMPP & API
const keyRoutes = [
  {
    name: 'SMPP & API',
    path: '/token',
    icon: ' uil-code',
    meta: { authRequired: false },
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        path: 'sms-gateway',
        name: 'SMS Gateway',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () => lazyLoadView(import('@views/pages/smpp_api/sms')),
      },
      {
        path: 'ipverification',
        name: 'IP Verification',
        meta: { authRequired: true, product: 'ip_verification' },
        component: () =>
          lazyLoadView(import('@views/pages/smpp_api/ipverification')),
      },
    ],
  },
]

// options
const optionsRoutes = [
  {
    name: 'Options',
    path: '/options',
    icon: 'uil-cog',
    meta: { authRequired: true, adminRequired: true },
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        path: 'general',
        name: 'General',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/options/general')),
      },
      {
        path: 'emailtemplate',
        name: 'Email Template',
        meta: { authRequired: true, adminRequired: true },
        component: () =>
          lazyLoadView(import('@views/pages/options/email-template')),
      },
      {
        path: 'smstemplate',
        name: 'SMS Template',
        meta: { authRequired: true, adminRequired: true },
        component: () =>
          lazyLoadView(import('@views/pages/options/sms-template')),
      },
      {
        path: 'sender',
        name: 'Security',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/options/ip-view')),
      },  
    ],
  },
]
// options
const SecurityRoute = [
  {
    name: 'Security',
    path: '/security',
    icon: 'uil-cloud-lock',
    meta: { authRequired: true, adminRequired: true },
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        path: 'whitelist',
        name: 'Whitelist',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/options/ip-view')),
      }, 
      {
        path: 'blacklist',
        name: 'Blacklist',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/options/ip-view-blacklist')),
      }, 
    ],
  },
]

// campaigns
const campaignRoutes = [
  {
    name: 'Send SMS',
    path: '/bulksms',
    icon: 'uil-comments-alt',
    meta: { authRequired: true, product: 'sms_gateway' },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Campaign',
        path: 'campaign',
        meta: { authRequired: true, product: 'sms_gateway' },
        badge: {
          text: '1',
          varient: 'success',
        },
        component: () => lazyLoadView(import('@views/pages/campaign')),
      },
      {
        path: 'draft',
        name: 'Draft',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () => lazyLoadView(import('@views/pages/campaign/draft')),
      },
      {
        path: 'template',
        name: 'Template',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () => lazyLoadView(import('@views/pages/template')),
      },
      {
        path: 'trash',
        name: 'Trash',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () => lazyLoadView(import('@views/pages/campaign/trash')),
      },
    ],
  },
]

// product
const product = [
  {
    name: 'Product',
    path: '/product',
    icon: ' uil-cart',
    meta: { authRequired: true },
    component: () => lazyLoadView(import('@views/pages/product/product')),
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
  },
]

// phonebook

const phonebookRoutes = [
  {
    path: '/phonebook',
    name: 'Phonebook',
    icon: ' uil-diary ',
    meta: { authRequired: true, product: 'sms_gateway' },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Contact',
        path: 'contact',
        meta: { authRequired: true, product: 'sms_gateway' },
        badge: {
          text: '1',
          varient: 'success',
        },
        component: () => lazyLoadView(import('@views/pages/phonebook')),
      },
      {
        path: 'group',
        name: 'Group',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () => lazyLoadView(import('@views/pages/phonebook/group')),
      },
    ],
  },
]

// account
const accountRoutes = [
  {
    path: '/account',
    name: 'Account',
    icon: 'uil-chat-bubble-user',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Users',
        path: 'users',
        meta: { authRequired: true, parentRequired: true },
        badge: {
          text: '1',
          varient: 'success',
        },
        component: () => lazyLoadView(import('@views/pages/account/user')),
      },
      {
        name: 'Groups',
        path: 'groups',
        meta: { authRequired: true, parentRequired: true },
        component: () => lazyLoadView(import('@views/pages/group')),
      },
      {
        name: 'Profile',
        path: 'profile',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/account/profile')),
      },
      {
        name: 'Profile',
        path: 'profile/:id',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/account/profile')),
      },
      {
        name: 'SMSToken',
        path: 'token/sms-gateway',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/smpp_api/sms')),
      },
      {
        name: 'IpToken',
        path: 'token/ipverification',
        meta: { authRequired: true },
        component: () =>
          lazyLoadView(import('@views/pages/smpp_api/ipverification')),
      },
    ],
  },
]

// account role
const roleRoutes = [
  {
    path: '/role',
    name: 'Role',
    icon: 'uil-users-alt',
    meta: { authRequired: true },
    // create a container component
    component: () => lazyLoadView(import('@views/pages/role/index')),
  },
]

// senderid
const senderRoutes = [
  {
    path: '/sender',
    name: 'Sender',
    icon: 'uil-message',
    header: 'Settings',
    meta: { authRequired: true, product: 'sms_gateway' },
    // create a container component
    component: () => lazyLoadView(import('@views/pages/sender')),
  },
]

// traffic
const SMSGatewayReportRoutes = [
  {
    header: 'Reports',
    path: '/sms-gateway',
    name: 'SMS Gateway',
    icon: 'uil-envelopes',
    meta: { authRequired: true, product: 'sms_gateway' },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Campaigns',
        path: 'campaign-report',
        icon: 'uil-chart-line',
        meta: { authRequired: true, product: 'sms_gateway' },
        // create a container component
        component: () => lazyLoadView(import('@views/pages/campaign-report')),
      },
      {
        path: 'transaction',
        name: 'Transaction',
        icon: 'uil-comment-alt-chart-lines',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () =>
          lazyLoadView(import('@views/pages/monitor/traffic-report')),
      },
      {
        name: 'Usage',
        path: 'usage',
        icon: 'uil-chart-growth',
        meta: { authRequired: true, product: 'sms_gateway' },
        component: () =>
          lazyLoadView(import('@views/pages/monitor/credit-usage')),
      },
    ],
  },
]

const IpverificationReportRoutes = [
  {
    name: 'IP Verification',
    path: '/ip-verification',
    icon: 'uil-user-check',
    meta: { authRequired: true, product: 'ip_verification' },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Transaction_',
        path: 'transaction',
        icon: 'uil-comment-alt-chart-lines',
        meta: { authRequired: true, product: 'ip_verification' },
        component: () =>
          lazyLoadView(
            import('@views/pages/ipverification-report/transaction/index')
          ),
      },
      {
        name: 'Usage_',
        path: 'usage',
        icon: 'uil-chart-growth',
        meta: { authRequired: true, product: 'ip_verification' },
        component: () =>
          lazyLoadView(
            import('@views/pages/ipverification-report/usage/index')
          ),
      },
    ],
  },
]

// support page

const supportRoute = [
  {
    path: '/support',
    meta: { authRequired: true },
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        path: '/',
        name: 'Support',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/support')),
        props: (route) => ({ user: store.state.auth.currentUser || {} }),
      },
      {
        path: 'rest',
        name: 'rest',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/support/detail')),
        props: (route) => ({ data: { name: 'rest', title: 'REST API' } }),
      },
      {
        path: 'http',
        name: 'http',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/support/detail')),
        props: (route) => ({ data: { name: 'http', title: 'HTTP API' } }),
      },
      {
        path: 'smpp',
        name: 'smpp',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/support/detail')),
        props: (route) => ({ data: { name: 'smpp', title: 'SMPP' } }),
      },
      {
        path: 'ip',
        name: 'ip',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/support/detail')),
        props: (route) => ({ data: { name: 'ip', title: 'IP Verification' } }),
      },
    ],
  },
]

// announcement page
const announcementRoute = [
  {
    header: 'Supports',
    path: '/announcement',
    name: 'Announcement',
    icon: 'uil-bell',
    meta: { authRequired: true, product: 'announcement' },
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Announcement',
        path: 'announcement-list',
        meta: { authRequired: true, product: 'announcement' },

        component: () =>
          lazyLoadView(import('@views/pages/announcement/index')),
        props: (route) => ({ type: 'public' }),
      },
      {
        name: 'Drafts',
        path: 'draft-list',
        meta: { authRequired: true, product: 'announcement' },
        component: () =>
          lazyLoadView(import('@views/pages/announcement/draft')),
        props: (route) => ({ type: 'draft' }),
      },
      {
        name: 'Trashes',
        path: 'trash-list',
        meta: { authRequired: true, product: 'announcement' },
        component: () =>
          lazyLoadView(import('@views/pages/announcement/trash')),
        props: (route) => ({ type: 'trash' }),
      },
      {
        name: 'Category',
        path: 'category-list',
        meta: { authRequired: true, product: 'announcement' },
        component: () =>
          lazyLoadView(import('@views/pages/announcement/category')),
      },
    ],
  },
]

const AllUserAnnRoute = [
  {
    path: '/notification',
    name: 'UserAnnouncemences',
    icon: 'uil-users-alt',
    meta: { authRequired: true },
    component: () =>
      lazyLoadView(import('@views/pages/notification/notifications')),
  },
  {
    path: '/notification/:id',
    name: 'UserAnnouncement',
    icon: 'uil-users-alt',
    meta: { authRequired: true },
    component: () =>
      lazyLoadView(import('@views/pages/notification/notification-detail')),
  },
]

// billing for admin
const billingRoutesAdmin = [
  {
    path: '/billing',
    name: 'Billing',
    icon: 'uil-usd-circle',
    meta: { authRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Topup',
        path: 'topup',
        meta: { authRequired: true },
        badge: {
          text: '1',
          varient: 'success',
        },
        component: () => lazyLoadView(import('@views/pages/topup/index')),
      },
      {
        name: 'Package',
        path: 'package',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/package/index')),
      },
      {
        name: 'Rate',
        path: 'rate',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/topup/rate')),
      },
      {
        name: 'Tax',
        path: 'tax',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/topup/tax')),
      },
    ],
  },
]

// // billing for auth
const billingRoutesAuth = [
  {
    path: '/billing',
    name: 'Billing',
    icon: 'uil-usd-circle',
    meta: { authRequired: true, parentRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Topup',
        path: 'topup',
        meta: { authRequired: true },
        badge: {
          text: '1',
          varient: 'success',
        },
        component: () => lazyLoadView(import('@views/pages/topup/index')),
      },
      {
        name: 'Package',
        path: 'package',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/package/index')),
      },
      {
        name: 'Rate',
        path: 'rate',
        meta: { authRequired: true },
        component: () => lazyLoadView(import('@views/pages/topup/rate')),
      },
      {
        name: 'Tax',
        path: 'tax',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/topup/tax')),
      },
    ],
  },
]

const authProtectedRoutes = [
  ...dashboardRoutes,
  ...campaignRoutes,
  ...keyRoutes,
  ...accountRoutes,
  ...phonebookRoutes,
  ...roleRoutes,
  ...senderRoutes,
  ...billingRoutesAuth,
  ...product,
  ...SMSGatewayReportRoutes,
  ...IpverificationReportRoutes,
  ...supportRoute,
  ...AllUserAnnRoute,
]

const routingRoutes = [
  {
    name: 'Routing',
    path: '/routing',
    icon: 'uil-exchange',
    meta: { authRequired: true, adminRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      {
        name: 'Carrier',
        path: 'carrier',
        meta: { authRequired: true },
        badge: {
          text: '1',
          varient: 'success',
        },
        component: () => lazyLoadView(import('@views/pages/account/carrier')),
      },
      {
        name: 'Country',
        path: 'country',
        icon: 'target',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/country')),
      },
      {
        name: 'Operator',
        path: 'operator',
        icon: 'target',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/operator')),
      },
      {
        name: 'Route',
        path: 'route',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/route')),
      },
      {
        name: 'Route Group',
        path: 'route-group',
        meta: { authRequired: true, adminRequired: true },
        component: () => lazyLoadView(import('@views/pages/route-group')),
      },
    ],
  },
]

// clone admin
const adminRoute = [
  {
    path: '/admin',
    name: 'Admin',
    icon: 'upload-cloud',
    meta: { authRequired: true, adminRequired: true },
    // create a container component
    component: {
      render(c) {
        return c('router-view')
      },
    },
    props: (route) => ({ user: store.state.auth.currentUser || {} }),
    children: [
      cloneDeep(...dashboardRoutes),
      cloneDeep(...campaignRoutes),
      cloneDeep(...keyRoutes),
      cloneDeep(...accountRoutes),
      cloneDeep(...phonebookRoutes),
      cloneDeep(...roleRoutes),
      cloneDeep(...senderRoutes),
      cloneDeep(...billingRoutesAdmin),
      cloneDeep(...routingRoutes),
      ...optionsRoutes,
      ...SecurityRoute,
      cloneDeep(...activityRoutes),
      cloneDeep(...SMSGatewayReportRoutes),
      cloneDeep(...IpverificationReportRoutes),
      cloneDeep(...supportRoute),
      ...announcementRoute,
      cloneDeep(...AllUserAnnRoute),
    ],
  },
]

// void duplicate route
adminRoute[0].children.forEach((route) => {
  route.name = `admin_${route.name}`
  route.meta.adminRequired = true
  route.path = route.path.replace('/', '')
  if (hasChild(route)) {
    route.children.forEach((childRoute) => {
      childRoute.name = `admin_${childRoute.name}`
      route.meta.adminRequired = true
    })
  }
})

const allRoutes = [
  ...authRoutes,
  ...authProtectedRoutes,
  ...errorPagesRoutes,
  ...adminRoute,
]

export { allRoutes, authProtectedRoutes, adminRoute }

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//
function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    loading: require('@components/_loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 400,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    // error: require('@views/_timeout').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 10000,
  })

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children)
    },
  })
}

function hasChild(item) {
  return item && item.children !== undefined ? item.children.length > 0 : false
}
